// @ts-strict-ignore
import {ReportName} from 'common/report/constants';
import {DisplayBookmark} from 'common/report/dashboards/types';
import {appApiUrl} from 'common/report/queries/utils';
import {apiClient} from 'common/util/api-client';
import {FetchOptions} from 'common/util/shared-client';

import {AIQuery, FlowsAIQuery, FunnelsAIQuery, InsightsAIQuery, RetentionAIQuery} from './types';
import {aiQueryToFlowsBookmark} from './flows';
import {aiQueryToInsightsBookmark} from './insights';
import {patchAIQuery} from './util';

export enum SparkGenerationErrorType {
  Generic = `generic`,
  ServerError = `server-error`,
  QueryApiError = `query-api-error`,
  EmptyMetrics = `empty-metrics`,
  UsageLimitHit = `usage-limit-hit`,
}

export class SparkGenerationError extends Error {
  declare errorType: SparkGenerationErrorType;
  constructor(errorType: SparkGenerationErrorType = SparkGenerationErrorType.Generic) {
    super(errorType);
    this.name = `SparkGenerationError`;
    this.errorType = errorType;
  }
}

export class SparkUsageLimitHitError extends SparkGenerationError {
  constructor(
    public usageLimit: number,
    public usage: number,
  ) {
    super(SparkGenerationErrorType.UsageLimitHit);
    this.name = `SparkUsageLimitHitError`;
  }
}

interface AIQueryResponse {
  query: AIQuery;
  usage: number;
  usage_limit: number;
}

/**
 * Generates a report from a natural language prompt.
 *
 * @param projectId - The project to make a report for.
 * @param prompt - The natural language prompt.
 * @param forceReportType - Force the AI to create a specific report type rather than it choosing.
 */
export async function generateSparkReport(
  projectId: number,
  workspaceId: number,
  prompt: string,
  options: FetchOptions = {},
  reportContext?: AIQuery,
  forceReportType?: ReportName,
): Promise<{
  aiQuery: AIQuery;
  usage: number;
  usageLimit: number;
  bookmark: DisplayBookmark;
}> {
  const url = appApiUrl(`ai/generate-report`, {projectId, workspaceId});

  try {
    const response = await apiClient.post<AIQueryResponse>(url, {prompt, reportContext, forceReportType}, options);

    const query = response.query;

    if (!query.metrics?.length) {
      throw new SparkGenerationError(SparkGenerationErrorType.EmptyMetrics);
    }

    return {
      aiQuery: query,
      usage: response.usage,
      usageLimit: response.usage_limit,
      bookmark: aiQueryToBookmark(query),
    };
  } catch (err) {
    if (err.statusCode === 429) {
      throw new SparkUsageLimitHitError(err.response.error.usage_limit, err.response.error.usage);
    }

    throw new SparkGenerationError(SparkGenerationErrorType.ServerError);
  }
}

/**
 * Turns an AI query report into a real bookmark.
 */
function aiQueryToBookmark(aiQuery: AIQuery): DisplayBookmark {
  patchAIQuery(aiQuery);

  switch (aiQuery.report.type) {
    case `insights`:
    case `funnels`:
    case `retention`:
      return aiQueryToInsightsBookmark(aiQuery as InsightsAIQuery | FunnelsAIQuery | RetentionAIQuery);
    case `flows`:
      return aiQueryToFlowsBookmark(aiQuery as FlowsAIQuery);
  }

  throw new Error(`Could not create bookmark from AI query: ${aiQuery}`);
}
